import React, { useEffect, useState } from 'react';
import { Collapse, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import ScheduleInstallation from './ScheduleInstallation';
import CartInformation from '../../common/CartInformation';
import CompatibilityCheck from './CompatibilityCheck';
import EnterInformation from './EnterInformation';
import PaymentDetail from './PaymentDetail'
import { useCartData, useLoading, useMbox, useServiceablitityData, useSessionData ,useStepsData } from '../../../hooks/order';
import { useDispatch } from 'react-redux';
import { CheckScheduleAppt, setRtsVisible } from '../../../store/action/OrderAction';
import TermsInfo from '../../common/TermsInfo';
import InstalltionInfo from '../../common/InstalltionInfo';
import { Checkbox } from 'antd';
import { getBaseUrl, getCallLogId } from '../../../common/common';
import { BackdropLoader } from '../../common/Loader';
import { getConfigs } from '../../../config';
import CreateAccount from './CreateAccount';
import RtsModal from '../../common/RtsModal';
import AccountExistsModal from '../../common/AccountExistsModal';
import { useCheckoutSessionGuard, useCheckoutBundle, initCheckoutTfnAndSchedule } from '../../../hooks/useCheckout';
import LanguageToggle from '../../Layout/LanguageToggle';
const config = getConfigs();

export const RESERVATION_SECONDS = 15 * 60;
export const getPanelKeys = (showCreateAccTile) => ({
  payment: showCreateAccTile ? '5' : '4',
  tc: showCreateAccTile ? '6' : '5',
});

const Checkout = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orderInfo } = useSessionData();
  const { isInternetAdded, internetPlan, isMobileAdded, isWhiAdded, isScheduleSuppressed, isSelfInstall} = useCartData();
  const { scheduleAppt, canScheduleAppt, serviceabilityCheck } = useServiceablitityData();
  const { plans = [], mobileProducts = [], fixedWirelessProducts = [] } = serviceabilityCheck;
  const { scheduleLoading } = useLoading();
  const { Panel } = Collapse;
  const callLogId = getCallLogId();
  const [tcAgree, setTcAgree] = useState(false);
  const submitOrderRef = React.useRef(() => {});
  const SHOW_SCHEDULE_APPT = config.SHOW_SCHEDULE_APPT && config.SHOW_SCHEDULE_APPT.toLowerCase() === 'true';
  const showSchedule = isInternetAdded && !isScheduleSuppressed && SHOW_SCHEDULE_APPT && canScheduleAppt?.toLowerCase() === 'yes';
  const showScheduleTile = showSchedule && scheduleAppt && scheduleAppt.length > 0 ? true : false;
  const [activeKey, setActiveKey] = useState('1');
  const [timeLeft, setTimeLeft] = useState(RESERVATION_SECONDS);
  const [timerStarted, setTimerStarted] = useState(false);
  const { isElnkMailboxReq } = useMbox();
  const [showCallSupportModal, setShowCallSupportModal] = useState(false);
  const childRef = React.createRef();
  const { paymentInfo } = useStepsData();
  useCheckoutSessionGuard();
  useCheckoutBundle();

  useEffect(() => {
    initCheckoutTfnAndSchedule({
      dispatch,
      plans,
      mobileProducts,
      fixedWirelessProducts,
      isInternetAdded,
      showSchedule,
      internetPlan,
      callLogId,
      isMobileAdded,
      isScheduleSuppressed,
      setActiveKey,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ELNKWEB-2948: the mount effect above only runs once, so if it skipped CheckScheduleAppt
  // because a CSI order started as Self Install, leaving self-install mid-checkout leaves
  // scheduleAppt empty and the Schedule tile has nothing to show even though showSchedule has
  // flipped back to true. Fetch install windows now instead of leaving the customer unable to
  // book the appointment their order now requires.
  //
  // Keyed on isSelfInstall rather than installType === 'professional': on the single-page
  // checkout a speed switch clears installType to '' (SAVE_PLAN_INFO, servRef changed), which
  // leaves scheduling required but unbookable AND drops csiOrder from the payload. Watching
  // the derived flag covers that, plan removal, and the plain toggle-back alike.
  const prevSelfInstallRef = React.useRef(isSelfInstall);
  useEffect(() => {
    const leftSelfInstall = prevSelfInstallRef.current && !isSelfInstall;
    prevSelfInstallRef.current = isSelfInstall;
    if (!leftSelfInstall || !showSchedule || scheduleAppt?.length) {
      return;
    }
    dispatch(CheckScheduleAppt({
      userName: 'xmlc',
      serviceReference: internetPlan.servRef,
      callLogId
    })).then(res => {
      if (res.value.data.installWindow && res.value.data.installWindow.length > 0) {
        setActiveKey('1');
      } else {
        dispatch(setRtsVisible(true));
      }
    }).catch(() => {
      dispatch(setRtsVisible(true));
    });
  }, [isSelfInstall, showSchedule]);

  const showCreateAccTile = config.SHOW_PSWD_CQAS === 'true' ? true : isElnkMailboxReq;
  const { payment: paymentPanelKey, tc: tcPanelKey } = getPanelKeys(showCreateAccTile);

  useEffect(() => {
    if (activeKey === paymentPanelKey && !timerStarted) {
      setTimeLeft(RESERVATION_SECONDS);
      setTimerStarted(true);
    }
  }, [activeKey, paymentPanelKey, timerStarted]);

  useEffect(() => {
    if (!timerStarted || timeLeft <= 0) {return;}
    const timerId = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timerId);
  }, [timeLeft, timerStarted]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleEditEmail = () => {
    childRef.current.emailEdit();
  }
  const isEnterInformationCompleted = Boolean(
    orderInfo?.firstName?.trim()
    && orderInfo?.lastName?.trim()
    && orderInfo?.email?.trim()
    && orderInfo?.phoneNum
  );
  const isPaymentCompleted = paymentInfo?.paymentType === 'bankDraft'
    ? Boolean(paymentInfo?.abaNum && paymentInfo?.bankAccountNum)
    : Boolean(paymentInfo?.encryptedCcn && paymentInfo?.expirationDate);

  const handleAccordionChange = (key) => {
    if (!key) {return;}
    const nextKey = Array.isArray(key) ? key[0] : key;
    if (nextKey === paymentPanelKey && !isEnterInformationCompleted) {
      setActiveKey('3');
      return;
    }
    if (nextKey === tcPanelKey && !isPaymentCompleted) {
      setActiveKey(paymentPanelKey);
      return;
    }
    setActiveKey(nextKey);
  };



  return (
    <>
      {scheduleLoading && <BackdropLoader />}
      <RtsModal handleCheckout={() => setActiveKey('1')} />
      {!scheduleLoading && <div className={'elink-container p-top-5 height-big-screen'}>
        <div className="elnk-internet-heading result-heading">
          <h1 className="elnk-internet-heading__title">{isScheduleSuppressed ? t('checkout.hurryActivation') : t('checkout.hurryInstall')}</h1>
        </div>
        <div className="main-checkout">
          <Row>
            <Col span={24} md={16} lg={16} xl={16} className="responsive-width-set">
              <Collapse
                accordion
                defaultActiveKey={['1']}
                activeKey={activeKey}
                onChange={handleAccordionChange}
                className="checkout-accordion"
              >
                {showScheduleTile &&
                  <Panel key="1" header={
                    <>
                      <span>{isWhiAdded ? t('checkout.activationAppt') : t('checkout.installationAppt')}</span>
                      {parseInt(activeKey) > 1 &&
                        <span className="checkout_edit" onClick={() => setActiveKey('1')}>
                          {t('common.edit')}
                        </span>}
                    </>
                  }>
                    <p className="installation-info">{t('checkout.apptInstructions')}</p>

                    <ScheduleInstallation scheduleAppt={scheduleAppt} activeKey={activeKey} setActiveKey={setActiveKey} isMobileAdded={isMobileAdded} />
                  </Panel>
                }
                {isMobileAdded && <Panel header={
                  <>
                    <span>{t('checkout.deviceCompatibility')}</span>
                    {parseInt(activeKey) > 2 &&
                          <span>
                            <i className="fas fa-edit" onClick={() => setActiveKey(2)}></i>
                          </span>}
                  </>
                } className="pnl" key="2">
                  <CompatibilityCheck activeKey={activeKey} setActiveKey={setActiveKey} />
                </Panel>}
                <Panel key="3" header={
                  <>
                    <span>{t('checkout.yourInformation')}</span>
                    {parseInt(activeKey) > 3 &&
                      <span className="checkout_edit" onClick={() => setActiveKey('3')}>
                        {t('common.edit')}
                      </span>}
                  </>
                }>
                  <EnterInformation ref={childRef} activeKey={activeKey} setActiveKey={setActiveKey} setShowCallSupportModal={setShowCallSupportModal} showCreateAccTile={showCreateAccTile} />
                </Panel>
                {showCreateAccTile && (
                  <Panel
                    key="4"
                    header={
                      <>
                        <span>{t('checkout.createAccount')}</span>
                        {parseInt(activeKey) > 4 &&
                        <span className="checkout_edit" onClick={() => setActiveKey('4')}>
                          {t('common.edit')}
                        </span>}
                      </>
                    }>
                    <CreateAccount ref={childRef} activeKey={activeKey} setActiveKey={setActiveKey} setShowCallSupportModal={setShowCallSupportModal} />
                  </Panel>)}
                <Panel
                  header={
                    <>
                      <span>{t('checkout.paymentDetails')}</span>
                      {parseInt(activeKey) > parseInt(paymentPanelKey) &&
                        <span className="checkout_edit" onClick={() => setActiveKey(paymentPanelKey)}>
                          {t('common.edit')}
                        </span>}
                    </>
                  } className="pnl" key={paymentPanelKey}>
                  {!isScheduleSuppressed && (
                    <div className="payment-timing-info">
                      <i className="fa-solid fa-clock" style={{ color: '#57585C', fontSize: '16px',alignContent: 'center' }}></i>&nbsp;
                      <p>
                        {timeLeft > 0
                          ? t('checkout.reservedFor', {time: formatTime(timeLeft)})
                          : t('checkout.reservationExpired')}
                      </p>
                    </div>
                  )}
                  <PaymentDetail activeKey={activeKey} setActiveKey={setActiveKey} />
                </Panel>
                <Panel
                  header={
                    <>
                      <span>{t('checkout.termsConditions')}</span>
                      <TermsDropdown />
                    </>
                  }
                  className="pnl"
                  key={`${showCreateAccTile ? '6' : '5'}`}
                >
                  <TermsAndConditions
                    submitOrderRef={submitOrderRef}
                    tcAgree={tcAgree}
                    setTcAgree={setTcAgree}
                  />
                </Panel>
              </Collapse>
              {/* <p className="terms-conditions">*Plus applicable taxes and fees. Once installed, you will be charged for your first month&apos;s service.</p> */}
            </Col>
            <Col span={8} className="p-left-1">
              <CartInformation setActiveKey={setActiveKey} submitOrderRef={submitOrderRef} />
            </Col>

          </Row>
        </div>
      </div>}
      <AccountExistsModal visible={showCallSupportModal} onEditEmail={handleEditEmail} />
    </>
  )
}
const TermsDropdown = () => {
  return (
    <LanguageToggle/>
  );
};

export const TermsAndConditions = ({ submitOrderRef, tcAgree, setTcAgree }) => {
  const { t } = useTranslation();
  const { spanishLanguagePref, isSelfInstall, internetPlan } = useCartData();
  const { loading, errorMessage } = useLoading();

  const acceptableUsePolicyUrl = spanishLanguagePref
    ? `${getBaseUrl()}tcs/acceptable-use-policy-es/`
    : `${getBaseUrl()}tcs/acceptable-use-policy/`;
  const privacyPolicyUrl = spanishLanguagePref
    ? `${getBaseUrl()}tcs/privacy-policy-spanish/`
    : `${getBaseUrl()}tcs/privacy-policy/`;

  return (
    <div className="main-review-part">
      {/* ELNKWEB-2948: CSI self-install replaces the scheduled-installation step, so the
          activation details are the only place the customer is told what happens next.
          Rendered only for that case, leaving every other order's flow untouched. */}
      {isSelfInstall && <InstalltionInfo internetPlan={internetPlan} />}
      <div className="review-terms-condition" style={{ padding: 0 }}>
        <div className="terms-description-upper">
          <TermsInfo />
        </div>
      </div>

      <div
        onClick={() => !loading && setTcAgree(!tcAgree)}
        style={{ cursor: loading ? 'not-allowed' : 'pointer', userSelect: 'none' }}
      >
        <Checkbox
          checked={tcAgree}
          disabled={loading}
        >
          {t('checkout.tcAgreeText')}{' '}
          <a href={`${getBaseUrl()}tcs`} target="_blank" rel="noreferrer" className="tc-link" onClick={(e) => e.stopPropagation()}>
            {t('checkout.tcTerms')}
          </a>
          {t('checkout.tcIncluding')}{' '}
          <a
            href={acceptableUsePolicyUrl}
            target="_blank"
            rel="noreferrer"
            className="tc-link"
            onClick={(e) => e.stopPropagation()}
          >
            {t('checkout.tcAcceptableUse')}
          </a>
          {t('checkout.tcAnd')}{' '}
          <a
            href={privacyPolicyUrl}
            target="_blank"
            rel="noreferrer"
            className="tc-link"
            onClick={(e) => e.stopPropagation()}
          >
            {t('checkout.tcPrivacy')}
          </a>
          {t('checkout.tcEnd')}
        </Checkbox>
      </div>

      <div className="m-t-20 payment-submit">
        {errorMessage && (
          <div className="red-color p-left-right-25">
            {t('checkout.tcError')}
          </div>
        )}
        <button
          className="blue-button"
          disabled={!tcAgree || loading}
          onClick={() => submitOrderRef.current && submitOrderRef.current(true)}
        >
          {loading ? t('common.processing') : t('common.placeOrder')}
        </button>
      </div>
    </div>
  );
};

export default Checkout;

